import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, API } from "../../lib/api";
import { eur } from "../../lib/format";
import { Plus, Search, Edit, Trash2, Copy, Download, Sparkles, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get("/admin/products", { params: { search } }).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [search]);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este producto?")) return;
    await adminApi.delete(`/admin/products/${id}`);
    toast.success("Producto eliminado");
    load();
  };
  const dup = async (id) => {
    await adminApi.post(`/admin/products/${id}/duplicate`);
    toast.success("Producto duplicado");
    load();
  };
  const togglePub = async (p) => {
    await adminApi.put(`/admin/products/${p.id}`, { ...p, published: !p.published });
    load();
  };
  const aiSeo = async (id) => {
    toast.loading("Generando SEO con IA...", { id: "ai" });
    try {
      await adminApi.post(`/admin/products/${id}/ai-seo`);
      toast.success("SEO generado con IA", { id: "ai" });
    } catch {
      toast.error("Error al generar SEO", { id: "ai" });
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="font-heading font-bold text-3xl tracking-tighter">Productos</h1>
        <div className="flex gap-2">
          <a href={`${API}/admin/export/products`} className="inline-flex items-center gap-2 border border-border rounded-sm px-3 py-2 text-sm hover:border-brand transition-colors" data-testid="export-products"><Download className="h-4 w-4" /> Exportar</a>
          <button onClick={() => nav("/admin/productos/nuevo")} className="inline-flex items-center gap-2 bg-brand text-white rounded-sm px-4 py-2 text-sm font-semibold hover:bg-brand-dark transition-colors" data-testid="new-product-btn"><Plus className="h-4 w-4" /> Nuevo producto</button>
        </div>
      </div>

      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o referencia..." className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-primary" data-testid="product-search" />
      </form>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-brand-ink text-white">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Referencia</th>
              <th className="text-left px-3 py-2 font-medium">Nombre</th>
              <th className="text-right px-3 py-2 font-medium">Precio</th>
              <th className="text-center px-3 py-2 font-medium">Estado</th>
              <th className="text-right px-3 py-2 font-medium">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Cargando...</td></tr> :
             products.length === 0 ? <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">Sin productos.</td></tr> :
             products.map((p, i) => (
              <tr key={p.id} className={i % 2 ? "bg-secondary/40" : "bg-white"} data-testid={`product-row-${p.id}`}>
                <td className="px-3 py-2 font-mono text-xs">{p.reference}</td>
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2 text-right font-medium">{eur(p.price)}</td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => togglePub(p)} className={`text-xs px-2 py-0.5 rounded-sm ${p.published ? "bg-emerald-100 text-emerald-700" : "bg-secondary text-muted-foreground"}`}>
                    {p.published ? "Publicado" : "Oculto"}
                  </button>
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => aiSeo(p.id)} title="Generar SEO IA" className="p-1.5 hover:text-brand" data-testid={`ai-seo-${p.id}`}><Sparkles className="h-4 w-4" /></button>
                    <button onClick={() => dup(p.id)} title="Duplicar" className="p-1.5 hover:text-brand"><Copy className="h-4 w-4" /></button>
                    <button onClick={() => nav(`/admin/productos/${p.id}`)} title="Editar" className="p-1.5 hover:text-brand" data-testid={`edit-product-${p.id}`}><Edit className="h-4 w-4" /></button>
                    <button onClick={() => del(p.id)} title="Eliminar" className="p-1.5 hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
